import { useTheme } from "@emotion/react";
import { observer } from "mobx-react-lite";
import { FC } from "react";
import { ToastContainer } from "react-toastify";
import { css } from "@/domains/emotion";
import "react-toastify/dist/ReactToastify.css";
import { MDSTextVariant } from "@/mds";

interface GlobalToastContainerProps {}

const DEFAULT_TOAST_TIMEOUT = 2_500;

/**
 * @deprecated - Use the newer MDS components instead.
 *
 * - If something doesn't exist yet, please create a new MDS component and
 *   have it reviewed by the team!
 *
 * - If something needs to hook into our `stores`, add it to the /components directory.
 *
 * - If something is a one-off; please colocate it with the component/page that
 *   needs it.
 *
 * - If you have questions, please ask in the #hub-eng-web-team Slack channel.
 */
export const GlobalToastContainer: FC<GlobalToastContainerProps> = observer(() => {
  const theme = useTheme();
  const globalToastStyleOverrides = css({
    "--toastify-toast-width": "0",

    ".Toastify__toast-container": {
      position: "fixed",

      ".Toastify__toast": {
        backgroundColor: `${theme.colors.surface.toast} !important`,
        borderRadius: `${theme.borderRadius.large} !important`,
        border: `1px solid ${theme.colors.border.surface} !important`,
        boxShadow: `${theme.shadows.surface} !important`,
        color: `${theme.colors.text.primary} !important`,
        display: "inline-flex",
        fontSize: `${theme.fontSizes[MDSTextVariant.TextS]} !important`,
        fontWeight: theme.fontWeights.regular,
        gap: theme.spacing["4"],
        maxWidth: "min(800px, 100vw - 48px)",
        minWidth: "340px",
        paddingBlock: theme.spacing["2"],
        paddingInline: theme.spacing["4"],
        width: "max-content",

        "@media only screen and (max-width: 480px)": {
          maxWidth: "100vw",
        },

        ".Toastify__toast-body": {
          padding: 0,
        },
      },
    },
  });

  return (
    <div className={globalToastStyleOverrides}>
      <ToastContainer
        position="bottom-center"
        autoClose={DEFAULT_TOAST_TIMEOUT}
        hideProgressBar
        icon={false}
        closeButton={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        stacked
        pauseOnHover
        theme="dark"
        className={globalToastStyleOverrides}
      />
    </div>
  );
});
